import { z } from 'zod';

import { isMoney, type Money } from '@nucleus-commerce/core';

import { isDateInput, isFutureDay } from './gift-card-rules';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** A new expiry must be a future day; `unchanged` is the card's own day, kept as it is. */
const expiry = (unchanged = '') =>
  z
    .string()
    .refine((v) => v === '' || isDateInput(v), { error: 'giftCards:validation.date' })
    .refine((v) => v === '' || v === unchanged || !isDateInput(v) || isFutureDay(v), {
      error: 'giftCards:validation.expiryPast',
    });

const note = z.string().trim().max(500, { error: 'giftCards:validation.noteLength' });
const message = z.string().trim().max(2000, { error: 'giftCards:validation.messageLength' });

export const issueGiftCardSchema = z
  .object({
    amount: z.custom<Money>(isMoney).nullable(),
    customerId: z.string(),
    recipientEmail: z
      .string()
      .trim()
      .refine((v) => v === '' || EMAIL.test(v), { error: 'giftCards:validation.email' }),
    expiresAt: expiry(),
    note,
    sendEmail: z.boolean(),
    message,
  })
  .superRefine((v, ctx) => {
    if (v.amount === null) {
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: 'giftCards:validation.amountRequired',
      });
    } else if (v.amount.amount <= 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['amount'],
        message: 'giftCards:validation.amountPositive',
      });
    }
  });

export type IssueGiftCardInput = z.input<typeof issueGiftCardSchema>;
export type IssueGiftCardOutput = z.output<typeof issueGiftCardSchema>;

/** `currentExpiry` is the card's expiry as a day input, so an expired card can keep it. */
export function giftCardEditSchema(currentExpiry: string) {
  return z.object({ expiresAt: expiry(currentExpiry), note });
}

export type GiftCardEditInput = z.input<ReturnType<typeof giftCardEditSchema>>;
export type GiftCardEditOutput = z.output<ReturnType<typeof giftCardEditSchema>>;

export const ADJUST_DIRECTIONS = ['add', 'remove'] as const;
export type AdjustDirection = (typeof ADJUST_DIRECTIONS)[number];

/**
 * The amount is typed as a positive figure and signed by `direction`; taking more than
 * `balance` (minor units) is refused here as the backend would.
 */
export function adjustGiftCardSchema(balance: number) {
  return z
    .object({
      direction: z.enum(ADJUST_DIRECTIONS),
      amount: z.custom<Money>(isMoney).nullable(),
      note,
    })
    .superRefine((v, ctx) => {
      if (v.amount === null) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'giftCards:validation.amountRequired',
        });
      } else if (v.amount.amount <= 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'giftCards:validation.amountPositive',
        });
      } else if (v.direction === 'remove' && v.amount.amount > balance) {
        ctx.addIssue({
          code: 'custom',
          path: ['amount'],
          message: 'giftCards:validation.belowZero',
        });
      }
    });
}

export type AdjustGiftCardInput = z.input<ReturnType<typeof adjustGiftCardSchema>>;
export type AdjustGiftCardOutput = z.output<ReturnType<typeof adjustGiftCardSchema>>;

/** `to` is one of the card's send targets (see `sendTargets`). */
export const sendGiftCardSchema = z.object({ to: z.string(), message });

export type SendGiftCardInput = z.input<typeof sendGiftCardSchema>;
export type SendGiftCardOutput = z.output<typeof sendGiftCardSchema>;
